import { Injectable } from '@angular/core';
import { Observable, of, from } from 'rxjs';
import { delay, map, switchMap, catchError } from 'rxjs/operators';
import { Warehouse, WarehouseSearchParams, WarehouseListResponse, Product, ProductSearchParams, ProductListResponse } from '../models/synchronization.model';
import { WarehouseControllerService, WarehouseQueryReqDTO, PageInfoWarehouseQueryResVO, WarehouseQueryResVO } from '@app/api/stock-service';
import { GoodsSkuControllerService, PageInfoGoodsSkuPageVO, GoodsSkuPageVO } from '@app/api/goods-service';

@Injectable({
  providedIn: 'root'
})
export class DataService {
  constructor(
    private warehouseControllerService: WarehouseControllerService,
    private goodsSkuControllerService: GoodsSkuControllerService
  ) {}

  /**
   * Process API response that might be a Blob or JSON
   */
  private processApiResponse<T>(response: any): Observable<T> {
    if (response instanceof Blob) {
      return from(response.text()).pipe(
        map((text) => {
          try {
            return JSON.parse(text);
          } catch (error) {
            throw new Error('Failed to parse API response');
          }
        })
      );
    }
    return of(response);
  }

  // Warehouse methods
  getWarehouses(params?: WarehouseSearchParams): Observable<WarehouseListResponse> {
    const queryParams: WarehouseQueryReqDTO = {
      page: params?.page || 0,
      size: params?.size || 20,
      code: params?.code ? [params.code] : undefined,
      name: params?.name,
      status: params?.status !== undefined ? params.status === 1 : undefined,
      typeCodes: ['logic']
    };

    return this.warehouseControllerService.queryWarehousePage(queryParams).pipe(
      switchMap((response) => this.processApiResponse<PageInfoWarehouseQueryResVO>(response)),
      map((response: PageInfoWarehouseQueryResVO) => {
        const warehouses: Warehouse[] = (response.pageContent || []).map((warehouse: WarehouseQueryResVO, index: number) => ({
          id: warehouse.id || 0,
          code: warehouse.code || '',
          name: warehouse.name || '',
          type: warehouse.typeCode || '',
          status: warehouse.status ? 1 : 0,
          serialNumber: (response.number || 0) * (response.size || 20) + index + 1
        }));

        return {
          content: warehouses,
          totalElements: response.totalElements || 0,
          totalPages: response.totalPages || 0,
          size: response.size || 20,
          number: response.number || 0,
          first: response.number === 0,
          last: (response.number || 0) >= (response.totalPages || 1) - 1,
          numberOfElements: response.numberOfElements || 0,
          empty: response.empty || false
        };
      }),
      catchError((error) => {
        console.error('Error fetching warehouses:', error);
        return of({
          content: [],
          totalElements: 0,
          totalPages: 0,
          size: 20,
          number: 0,
          first: true,
          last: true,
          numberOfElements: 0,
          empty: true
        });
      })
    );
  }

  getWarehouseById(id: number): Observable<Warehouse | null> {
    // For now, we don't have a single warehouse API, so we'll search by ID
    return this.getWarehouses({ page: 0, size: 1 }).pipe(
      map(response => {
        const warehouse = response.content.find(w => w.id === id);
        return warehouse || null;
      })
    );
  }

  // Product methods
  getProducts(params?: ProductSearchParams): Observable<ProductListResponse> {
    const searchParams = params || {};
    const { productName, productCode, skuCode, page = 1, size = 20 } = searchParams;

    return this.goodsSkuControllerService.getList3(
      undefined, // keyword
      productName, // spuName
      productCode, // spuCode
      undefined, // spuType
      0, // setFlag: physical inventory can only adjust ordinary SKUs
      undefined, // categoryIdList
      [3], // status: 3-Effective
      undefined, // skuName
      undefined, // searchMode
      skuCode, // skuCode
      undefined, // startDate
      undefined, // endDate
      undefined, // skuIds
      undefined, // lastUpdatedAfter
      undefined, // channelShopId
      undefined, // warehouseCode
      1, // showFlag - only Active SKUs can be added to a new stock adjustment
      page - 1, // page (convert to 0-based)
      size, // size
      undefined, // sortBy
      undefined // ascending
    ).pipe(
      switchMap((response) => this.processApiResponse<PageInfoGoodsSkuPageVO>(response)),
      map((response: PageInfoGoodsSkuPageVO) => {
        const ordinarySkus = (response.pageContent || []).filter(
          (product: GoodsSkuPageVO) => product.setFlag !== 1
        );

        const products: Product[] = ordinarySkus.map((product: GoodsSkuPageVO, index: number) => ({
          id: product.id || 0,
          serialNumber: (response.number || 0) * (response.size || 20) + index + 1,
          categoryName: product.categoryName || '',
          spuCode: product.spuCode || '',
          spuName: product.spuName || '',
          skuCode: product.skuCode || '',
          skuName: product.skuName || '',
          productType: 'Standard Item'
        }));

        return {
          content: products,
          totalElements: response.totalElements || 0,
          totalPages: response.totalPages || 0,
          size: response.size || 20,
          number: response.number || 0,
          empty: response.empty || false
        };
      }),
      catchError((error) => {
        console.error('Error fetching products:', error);
        return of({
          content: [],
          totalElements: 0,
          totalPages: 0,
          size: 20,
          number: 0,
          empty: true
        });
      })
    );
  }

  getProductById(id: number): Observable<Product | null> {
    // For now, we don't have a single product API, so we'll search by ID
    return this.getProducts({ page: 1, size: 1 }).pipe(
      map(response => {
        const product = response.content.find(p => p.id === id);
        return product || null;
      })
    );
  }
}
